// Copy for the Screen Inspector page. `{name}` placeholders are filled by fmt().
const translations = {
    en: {
        language: 'Language',
        states: { ready: 'Ready', running: 'Running', fault: 'Fault' },
        stateLabel: 'Device state',
        deck: {
            prev: 'Previous',
            next: 'Next',
            labels: ['Screen Inspector', 'Part 1 · The library', 'How it works', 'Question types', 'Why crop', 'Reliability', 'Part 2 · MCP server', 'Cut the page', 'Stored by name', 'All at once', 'Trial gate', 'Code & releases'],
        },

        hero: {
            name: 'Screen Inspector',
            title: 'Tests that can read the screen.',
            sub: 'For industrial HMIs with no DOM. A small vision model on your own machine answers one question at a time.',
            link: 'See how it works',
            code: 'View the code',
            releases: 'Releases',
            caption: 'The panel on this page is a mock drawn for the demo.',
        },

        how: {
            eyebrow: 'How it works',
            title: 'One component. Five steps.',
            sub: 'From a screenshot to a value your test can assert on.',
            steps: [
                { title: 'Take a screenshot.', body: 'No DOM, just pixels. The image never leaves the machine.', detail: '1280 × 800' },
                { title: 'Find the component.', body: 'Template matching on an anchor that never changes with state. No model needed.', detail: 'anchor match 1.000 · studies 06, 07' },
                { title: 'Cut it out.', body: 'Only this piece goes to the model, so nothing next to it can pull the answer off.', detail: '370 × 200 px · study 04' },
                { title: 'Ask one question.', body: 'In the exact words on the screen. The model replies with one of the options.', detail: 'only ask what the crop shows · study 09', question: 'Does the status show Off?' },
                { title: 'Get a value.', body: 'The reply is parsed into True or False, a number, or an option. Ready to assert.', detail: 'parsing pinned by offline tests · study 01' },
            ],
            model: 'qwen3-vl:4b · local',
        },

        types: {
            eyebrow: 'Question types',
            title: 'Five kinds of questions.',
            sub: 'Each one comes back as a typed value: an option, True or False, text, or a number.',
            names: {
                choose: 'Pick one',
                check: 'True / False',
                read: 'Read text',
                read_number: 'Read a number',
                choose_image: 'Match a picture',
            },
            question: 'Question',
            answer: 'Answer',
            model: 'About 1.2 s per question on an M4 Pro (study 08).',
            pixels: 'Matched by pixels in 0.04 s. The model was never asked (study 14).',
            rules: 'Answers use the option text, never A / B / C. An “Other” option is always there. No self-reported confidence (study 09).',
        },

        crop: {
            eyebrow: 'Why crop',
            title: 'Show it less. It sees more.',
            sub: 'On the Ready screen the pump is Off, but its Auto Mode switch is green. Given the whole screen, the model reads the switch.',
            whole: 'Whole screenshot',
            card: 'Only the card',
            says: 'Model says',
            score: '{n} / 17 correct',
            note: 'Outlining the card in red (12 / 17) or adding its coordinates (13 / 17) did not help, and zoom did not matter: the crop at 1× got 8 / 8. From study 04.',
        },

        part1: {
            kicker: 'Part 1',
            title: 'The library',
            body: 'A Python package that tests import. It finds a component on the screenshot, crops it, asks a local vision model one typed question and hands back a value. Screenshots never leave the machine, and no AI agent is involved when tests run.',
            facts: [
                ['For', 'Test and integration code'],
                ['When', 'Every time a test runs'],
                ['Does', 'Recognize, locate, click and type, page classes'],
                ['Install', 'pip install screen-inspector'],
            ],
        },

        part2: {
            kicker: 'Part 2',
            title: 'The MCP server',
            body: 'Writing these checks by hand is slow: crop sample images, build library entries, try one wording after another. The MCP server turns each step into a tool, so an AI agent such as Claude Code drives the library to do the work and writes plain Python at the end.',
            facts: [
                ['For', 'AI agents (Claude Code and other MCP clients)'],
                ['When', 'While tests are being designed'],
                ['Does', 'Explore pages, build entries, try questions, export page methods and tests'],
                ['Install', 'pip install "screen-inspector[mcp]"'],
            ],
            flow: [
                ['explore', 'map every page by clicking navigation only'],
                ['create_target', 'cut a page into named entries'],
                ['try_question', 'ask on several states and pass the gate'],
                ['export_reading · export_action', 'write page methods with their evidence'],
                ['export_test', 'combine page methods into a test'],
            ],
            bench: 'On a real test bench: 9 pages and 70 navigation links mapped, 102 skeleton methods generated.',
        },

        reliability: {
            eyebrow: 'Reliability',
            title: 'How far can a 4B model be trusted?',
            sub: 'qwen3-vl:4b on an Apple M4 Pro, temperature 0, about 1.2 s per question. Every number below comes from a research notebook.',
            items: [
                ['1404 / 1404', 'true/false checks on 108 real screenshots in 10 operating states; repeated inputs gave identical answers', 'Study 08'],
                ['209 / 209', 'number readings across four pages, decimals copied exactly as shown; discharge pressure alone 135 / 135', 'Studies 10, 14'],
                ['135 / 135', 'run-state questions that ask only for what is visible in the crop; shuffling the options changed nothing', 'Study 09'],
                ['17 / 17', 'device-card checks once only the card is cropped out, including the green switch that misleads on the full screen', 'Study 04'],
                ['43 / 43', 'look-only states such as switches and button colours, matched against reference images by pixels in 0.04 s', 'Study 14'],
                ['213 / 213', 'pixel answers under 9 simulated rendering changes; when unsure it hands the question to the model instead of guessing', 'Study 15'],
            ],
            index: 'All research notes',
            limits: 'All results: qwen3-vl:4b, temperature 0, 1280 × 800 screenshots of one interface. Look-only states (switches, button colours) are compared by pixels first, so the model is not asked to guess them.',
        },

        gate: {
            eyebrow: 'MCP · Trial gate',
            title: 'Tried before it’s written.',
            sub: 'try_question asks the same question on screenshots in different states. Only if every answer is right, and at least two different answers came up, does it become a page method.',
            columns: ['Screenshot', 'Expected', 'Got'],
            passed: 'Passed: 3 / 3 correct, 3 different answers',
            method: 'Exported page method',
            note: 'Tests only combine page methods. They run as plain Python, with no MCP and no AI.',
        },

        library: {
            eyebrow: 'MCP · The whole page',
            title: 'Cut once. Name everything.',
            sub: 'The AI cuts each page into named entries with create_target: every card, reading and button, with its sample image, anchor and region.',
            replay: 'Replay',
            legend: { component: 'Whole component', item: 'Single item', anchor: 'Anchor' },
            found: '{n} / {n} found · same positions',
            reference: '{n} entries on the reference screenshot',
            scanning: 'Matching every anchor…',
            storeEyebrow: 'MCP · The object library',
            dbTitle: 'Stored by name.',
            dbSub: 'Each entry lives in SQLite. A test never writes coordinates; it just asks for ui["overview.pressure_value"].',
            hint: 'Hover an entry to see its anchor.',
            columns: ['Name', 'Kind', 'Region', 'Score'],
            kinds: { component: 'whole', item: 'item' },
            anchorFrom: {
                title: 'Anchor: the card title. Region: the whole card.',
                row: 'Anchor: the row name. Region: this row only.',
                parent: 'Its look changes completely, so it is anchored on the card title.',
                icon: 'Anchor: the icon, away from the changing number.',
            },
            real: 'The real library has 76 entries, found on all 255 state screenshots.',
        },

        tree: {
            eyebrow: 'MCP · All at once',
            title: 'The whole page, one call.',
            sub: 'Once the page is in the library, everything to check is written as a tree. inspect_page follows the answers and returns the same shape.',
            run: 'Run again',
            running: 'Asking…',
            group: 'group',
            skipped: 'skipped',
            ifFalse: 'if False',
            note: 'Study 12: 78 / 78 answers on 6 screenshots, every branch followed as expected.',
            questions: {
                page: 'Which page is shown?',
                status: 'What run state is shown after STATUS?',
                pressure: 'What is the discharge pressure?',
                mode: 'Which mode does the Loading panel show?',
                full_load: 'Is the Full Load button highlighted?',
                no_alarms: 'Does it show No active alarms?',
                first_alarm: 'What does the first alarm say?',
            },
        },

        code: {
            eyebrow: 'Code & releases',
            title: 'Open on GitLab.',
            sub: 'The project and its latest release, read straight from the GitLab API.',
            description: 'Local vision-model checks for HMI screenshots, plus an MCP server that lets an AI build them.',
            stars: 'stars',
            forks: 'forks',
            updated: 'updated',
            latest: 'Latest release',
            sourceCode: 'Source code',
            openProject: 'Open in GitLab',
            allReleases: 'All releases',
            noRelease: 'No release yet.',
            snapshot: 'Snapshot from {date}',
        },

        footer: {
            built: 'Built with Python, Ollama, qwen3-vl:4b, NumPy, SQLite and MCP.',
            code: 'Source code on GitLab',
            note: 'The panel on this page is a mock; real screenshots stay private. Numbers come from the project’s research notes.',
        },
    },

    zh: {
        language: '语言',
        states: { ready: '就绪', running: '运行中', fault: '故障' },
        stateLabel: '设备状态',
        deck: {
            prev: '上一页',
            next: '下一页',
            labels: ['Screen Inspector', '第一部分 · 函数库', '怎么做', '题型', '为什么裁剪', '可靠性', '第二部分 · MCP 服务器', '整个页面', '按名字存', '一次问完', '试通门槛', '代码与发布'],
        },

        hero: {
            name: 'Screen Inspector',
            title: '让测试看懂屏幕。',
            sub: '给拿不到 DOM 的工业 HMI。本机的小视觉模型，一次只回答一个问题。',
            link: '看看怎么做的',
            code: '查看代码',
            releases: '发布版本',
            caption: '页面上的 HMI 是为演示画的示意图。',
        },

        how: {
            eyebrow: '怎么做',
            title: '一个组件，五步。',
            sub: '从一张截图，到一个能断言的值。',
            steps: [
                { title: '截一张图。', body: '没有 DOM，只有像素。截图不出本机。', detail: '1280 × 800' },
                { title: '找到组件。', body: '用不随状态变的 anchor 做模板匹配，不用模型。', detail: 'anchor 匹配得分 1.000 · 研究 06、07' },
                { title: '裁出来。', body: '只把这一块交给模型，旁边的东西带不偏它。', detail: '370 × 200 像素 · 研究 04' },
                { title: '问一个问题。', body: '照画面原文问，模型回答其中一个选项。', detail: '只问裁出来的图里看得见的东西 · 研究 09', question: '图片中的状态是否显示 Off？' },
                { title: '拿到一个值。', body: '回答解析成 True / False、数字或选项，直接断言。', detail: '解析规则由离线测试固定 · 研究 01' },
            ],
            model: 'qwen3-vl:4b · 本机',
        },

        types: {
            eyebrow: '题型',
            title: '五种问法。',
            sub: '每种都返回有类型的值：选项、True / False、文字或数字。',
            names: {
                choose: '选择题',
                check: '判断题',
                read: '读文字',
                read_number: '读数',
                choose_image: '图片选择题',
            },
            question: '问题',
            answer: '回答',
            model: 'M4 Pro 上每题约 1.2 秒（研究 08）。',
            pixels: '比像素 0.04 秒就有答案，没问模型（研究 14）。',
            rules: '回答选项原文，不用 A / B / C；总有一个 Other 选项；不让模型自报可信度（研究 09）。',
        },

        crop: {
            eyebrow: '为什么裁剪',
            title: '给得越少，看得越准。',
            sub: '就绪画面上泵是 Off，但它的 Auto Mode 开关是绿的。给整张截图，模型看的是那个开关。',
            whole: '整张截图',
            card: '只给这张卡片',
            says: '模型回答',
            score: '17 题对 {n} 题',
            note: '在整张图上画红框（12/17）、写坐标（13/17）都没用；放不放大也无所谓，不放大的裁剪 8/8。数据来自研究 04。',
        },

        part1: {
            kicker: '第一部分',
            title: '函数库',
            body: '一个测试代码直接 import 的 Python 包：在截图上找到组件、裁出来、问本机的视觉模型一个固定题型的问题，返回一个值。截图不出本机，测试运行时也不需要 AI。',
            facts: [
                ['给谁用', '测试代码、联调代码'],
                ['什么时候用', '每次跑测试'],
                ['做什么', '识别、定位、点击输入、页面类'],
                ['安装', 'pip install screen-inspector'],
            ],
        },

        part2: {
            kicker: '第二部分',
            title: 'MCP 服务器',
            body: '手写这些识别代码很慢：截样例图、建对象库条目、一遍遍试问法。MCP 服务器把每一步做成工具，让 AI（如 Claude Code）大量调用上面的函数库来完成，最后写出普通的 Python 代码。',
            facts: [
                ['给谁用', 'AI（Claude Code 等 MCP 客户端）'],
                ['什么时候用', '设计用例时'],
                ['做什么', '探索页面、建条目、试问法、导出页面方法和用例'],
                ['安装', 'pip install "screen-inspector[mcp]"'],
            ],
            flow: [
                ['explore', '只点导航，画出每一页'],
                ['create_target', '把一页切成有名字的条目'],
                ['try_question', '在几种状态上试问，过了门槛才算数'],
                ['export_reading · export_action', '写成页面方法，附上证据'],
                ['export_test', '用页面方法组合成用例'],
            ],
            bench: '在真实台架上：探出 9 页、70 条导航，生成 102 个骨架方法。',
        },

        reliability: {
            eyebrow: '可靠性',
            title: '4B 小模型靠得住吗？',
            sub: 'qwen3-vl:4b，Apple M4 Pro，temperature 0，每题约 1.2 秒。下面每个数字都来自一篇研究 notebook。',
            items: [
                ['1404 / 1404', '道判断题，108 张真实截图、10 种工况；同样的输入重复问，答案完全一致', '研究 08'],
                ['209 / 209', '四个页面的数字小图，小数位照原样写；其中排气压力 135/135', '研究 10、14'],
                ['135 / 135', '运行状态题：只问裁出来的图里看得见的字；选项顺序打乱也一样全对', '研究 09'],
                ['17 / 17', '设备卡片判断题：只把卡片裁出来，整张图上会带偏模型的绿色开关也不再干扰', '研究 04'],
                ['43 / 43', '开关、按钮颜色这类外观状态，和参照图逐像素比，每题 0.04 秒', '研究 14'],
                ['213 / 213', '模拟 9 种渲染变化，比像素有把握的题全对；没把握时交给模型，不硬猜', '研究 15'],
            ],
            index: '全部研究记录',
            limits: '以上结果都是 qwen3-vl:4b、temperature 0、同一套界面 1280×800 截图上得到的。外观状态（开关、按钮颜色）先比像素，不交给模型猜。',
        },

        gate: {
            eyebrow: 'MCP · 试通门槛',
            title: '试通了才写进代码。',
            sub: 'try_question 在不同状态的截图上问同一个问题。每张都答对、而且答案至少有两种，才会变成页面方法。',
            columns: ['截图', '期望', '回答'],
            passed: '试通：3/3 全对，覆盖 3 种答案',
            method: '导出的页面方法',
            note: '用例只组合页面方法，跑的是普通 Python，不经过 MCP，也不需要 AI。',
        },

        library: {
            eyebrow: 'MCP · 整个页面',
            title: '切一次，每块都有名字。',
            sub: 'AI 用 create_target 把整页切成有名字的条目：每张卡片、每个读数、每个按钮，带着样例图、anchor 和区域。',
            replay: '重放',
            legend: { component: '整块组件', item: '单项数据', anchor: 'anchor' },
            found: '{n} / {n} 个都找到 · 位置不变',
            reference: '参考截图上的 {n} 个条目',
            scanning: '正在匹配每个 anchor…',
            storeEyebrow: 'MCP · 对象库',
            dbTitle: '按名字存。',
            dbSub: '每个条目存在 SQLite 里。用例不写坐标，只写 ui["overview.pressure_value"]。',
            hint: '鼠标放到条目上，看它的 anchor。',
            columns: ['名字', '种类', '区域', '得分'],
            kinds: { component: '整块', item: '单项' },
            anchorFrom: {
                title: 'anchor：卡片标题；区域：整张卡片。',
                row: 'anchor：行名；区域：只有这一行。',
                parent: '外观会整个变，所以用所在卡片的标题当 anchor。',
                icon: 'anchor：图标本身，避开会变的数字。',
            },
            real: '真实对象库有 76 个条目，在 255 张各种状态的截图上全部找到。',
        },

        tree: {
            eyebrow: 'MCP · 一次问完',
            title: '整页，一次问完。',
            sub: '整页进了对象库以后，把要看的写成一棵树。inspect_page 按回答走分支，返回同样结构的结果。',
            run: '再问一遍',
            running: '正在问…',
            group: '分组',
            skipped: '跳过',
            ifFalse: 'False 时才问',
            note: '研究 12：6 张截图 78/78 全对，每个分支都按回答走。',
            questions: {
                page: '当前显示的是哪个页面？',
                status: 'STATUS 后面显示的运行状态是？',
                pressure: '排气压力的读数是多少？',
                mode: 'Loading 面板显示的模式是？',
                full_load: 'Full Load 按钮是否高亮？',
                no_alarms: '图片中是否显示 No active alarms？',
                first_alarm: '第一条报警的文字是什么？',
            },
        },

        code: {
            eyebrow: '代码与发布',
            title: '代码在 GitLab 上。',
            sub: '项目和最新的 release，直接从 GitLab API 读取。',
            description: '用本机视觉模型识别 HMI 截图的函数库，加上让 AI 来写识别代码的 MCP 服务器。',
            stars: '星标',
            forks: 'fork',
            updated: '更新于',
            latest: '最新发布',
            sourceCode: '源代码',
            openProject: '在 GitLab 打开',
            allReleases: '所有 release',
            noRelease: '还没有 release。',
            snapshot: '{date} 的快照',
        },

        footer: {
            built: '用到 Python、Ollama、qwen3-vl:4b、NumPy、SQLite 和 MCP。',
            code: 'GitLab 上的源代码',
            note: '页面上的 HMI 是示意图，真实截图不公开。数字来自项目的研究记录。',
        },
    },
};

export function fmt(text, vars = {}) {
    return text.replace(/\{(\w+)\}/g, (m, k) => (k in vars ? vars[k] : m));
}

export default translations;
